<script lang="ts">
import { Search } from '@element-plus/icons-vue';
import {
  defineComponent, PropType, ref, watch,
} from 'vue';
import { debounce } from '@/shared/utility-functions';

interface Option { label: string; value: any }
interface Chip { key: string; label: string; value: string }

// One toolbar row for CMS list pages: status tabs, active-filter chips, search and sort
// on one line. While rows are selected, search and sort are swapped for the #bulk slot.
export default defineComponent({
  name: 'CmsListToolbar',
  props: {
    tabs: { type: Array as PropType<Option[]>, default: () => [] },
    activeTab: { type: String, default: '' },
    tabsDisabled: { type: Boolean, default: false },
    chips: { type: Array as PropType<Chip[]>, default: () => [] },
    modelValue: { type: String, default: '' },
    searchPlaceholder: { type: String, default: 'Search...' },
    sortValue: { type: [Object, String], default: () => ({}) },
    sortOptions: { type: Array as PropType<Option[]>, default: () => [] },
    selectedCount: { type: Number, default: 0 },
  },
  emits: [
    'update:modelValue', 'update:sortValue', 'search', 'sort', 'tab-change', 'clear-selection',
    'remove-chip',
  ],
  setup(props, { emit }) {
    const search = ref(props.modelValue);
    const sort = ref(props.sortValue);

    const emitSearch = debounce((newValue: string) => {
      emit('update:modelValue', newValue);
      emit('search', newValue);
    }, 300);

    watch(search, (newValue) => emitSearch(newValue));

    watch(() => props.modelValue, (newValue) => {
      if (search.value !== newValue) search.value = newValue;
    });

    watch(() => props.sortValue, (newValue) => {
      sort.value = newValue;
    });

    function handleSortChange(value) {
      emit('update:sortValue', value);
      emit('sort', value);
    }

    return {
      Search, search, sort, handleSortChange, emit,
    };
  },
});
</script>

<template lang="pug">
.c-list-toolbar
  slot(name="start")
  .c-list-toolbar__tabs(v-if="tabs.length" role="tablist")
    button.c-list-toolbar__tab(
      v-for="tab in tabs"
      :key="tab.value"
      type="button"
      role="tab"
      :aria-selected="activeTab === tab.value"
      :class="{ 'c-list-toolbar__tab--active': activeTab === tab.value }"
      :disabled="tabsDisabled"
      @click="emit('tab-change', tab.value)"
    ) {{ tab.label }}

  .c-list-toolbar__chip(v-for="chip in chips" :key="chip.key")
    span.c-list-toolbar__chip-label {{ chip.label }}:
    span.c-list-toolbar__chip-value(:title="chip.value") {{ chip.value }}
    button.c-list-toolbar__chip-remove(
      type="button"
      :aria-label="`Remove ${chip.label} filter`"
      @click="emit('remove-chip', chip.key)"
    ) ✕

  template(v-if="selectedCount > 0")
    .c-list-toolbar__bulk
      span.c-list-toolbar__count {{ selectedCount }} selected
      slot(name="bulk")
      button.c-list-toolbar__clear(type="button" @click="emit('clear-selection')") Clear selection

  template(v-else)
    .c-list-toolbar__search
      el-input(
        v-model="search"
        :prefix-icon="Search"
        type="text"
        :placeholder="searchPlaceholder"
        clearable
      )
    el-select.c-list-toolbar__sort(
      v-if="sortOptions.length"
      v-model="sort"
      value-key="label"
      placeholder="Sort by"
      @change="handleSortChange"
    )
      el-option(
        v-for="item in sortOptions"
        :key="item.label"
        :label="item.label"
        :value="item"
      )

  slot(name="actions")
</template>

<style lang="scss" scoped>
@import '@/views/manage-users-and-organizations/crm-list';

.c-list-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid $border;

  &__tabs {
    display: flex;
    flex: none;
    max-width: 100%;
    height: 36px;
    align-items: center;
    background-color: $muted;
    border-radius: 14px;
    padding: 3px;
    overflow-x: auto;
  }

  &__tab {
    height: 100%;
    padding: 0 12px;
    border: 1px solid transparent;
    border-radius: 14px;
    background: transparent;
    font-size: 0.875rem;
    font-weight: 500;
    color: $muted-foreground;
    white-space: nowrap;
    cursor: pointer;
    transition: color 0.15s ease, background-color 0.15s ease;

    &:hover {
      color: $foreground;
    }

    &:focus-visible {
      outline: 2px solid $teal-accent;
      outline-offset: -2px;
    }

    &:disabled {
      cursor: default;
    }

    &--active {
      background-color: #fff;
      color: $foreground;
    }
  }

  &__chip {
    display: inline-flex;
    flex: 0 1 auto;
    min-width: 0;
    max-width: 280px;
    height: 30px;
    align-items: center;
    gap: 6px;
    padding: 0 6px 0 12px;
    border: 1px solid $teal-accent;
    border-radius: 15px;
    background: rgba(1, 190, 176, 0.08);
    font-size: 0.8125rem;
    color: $foreground;
  }

  &__chip-label {
    flex: none;
    color: $muted-foreground;
  }

  &__chip-value {
    overflow: hidden;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chip-remove {
    display: inline-flex;
    flex: none;
    width: 20px;
    height: 20px;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    background: transparent;
    font-size: 0.6875rem;
    color: $muted-foreground;
    cursor: pointer;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
      color: $foreground;
    }
  }

  &__search {
    flex: 1;
    min-width: 160px;

    :deep(.el-input) {
      --el-input-height: 36px;

      .el-input__wrapper {
        @include crm-control-skin;

        &:hover {
          box-shadow: none;
        }
      }

      .el-input__inner {
        height: 100%;
        font-size: 0.875rem;
        color: $foreground;

        &::placeholder {
          color: $muted-foreground;
        }
      }

      .el-input__prefix {
        color: $gray-400;
      }
    }
  }

  &__sort {
    width: 180px;

    :deep(.el-select__wrapper) {
      @include crm-control-skin;
      font-size: 0.875rem;

      .el-select__selected-item,
      .el-select__placeholder {
        color: $foreground;
      }
    }
  }

  &__bulk {
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    min-height: 36px;
  }

  &__count {
    font-size: 0.875rem;
    font-weight: 600;
    color: $foreground;
  }

  &__clear {
    border: 0;
    background: none;
    padding: 0;
    font-size: 0.875rem;
    color: $muted-foreground;
    cursor: pointer;

    &:hover {
      color: $foreground;
      text-decoration: underline;
    }
  }

  @media (max-width: 767px) {
    &__search,
    &__sort {
      width: 100%;
      flex: 1 1 100%;
    }
  }
}
</style>
